import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"

export const useConnectorAccountsStore = defineStore("connectorAccounts", {
  state: () => ({
    connectorAccounts: [],
    mapping: {},
    loading: false,
    syncing: false,
    saving: false,
    error: null
  }),

  getters: {
    /**
     * Get connector accounts as Select dropdown options
     * { label, value } where value is the external_id
     */
    connectorAccountOptions: (state) => {
      return state.connectorAccounts.map((account) => ({
        label: account.name,
        value: account.external_id
      }))
    },

    /**
     * Get a mapped external account for a given HotelOne account id
     */
    getMappedAccount: (state) => {
      return (accountId) => state.mapping[accountId] ?? null
    },

    /**
     * Count how many HotelOne accounts are mapped
     */
    mappedCount: (state) => {
      return Object.values(state.mapping).filter((v) => v !== null && v !== "")
        .length
    }
  },

  actions: {
    /**
     * Fetch connector's own accounts from the external system
     * GET /connectors/{id}/accounts
     */
    async fetchConnectorAccounts(connectorId) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.get(`/connectors/${connectorId}/accounts`)
        this.connectorAccounts = response.data.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Trigger a sync of accounts from the connector
     * POST /connectors/{id}/sync-accounts
     */
    async syncConnectorAccounts(connectorId) {
      this.syncing = true
      this.error = null
      try {
        const response = await apiV1.post(
          `/connectors/${connectorId}/sync-accounts`
        )
        // Refresh accounts after sync
        await this.fetchConnectorAccounts(connectorId)
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.syncing = false
      }
    },

    /**
     * Fetch current account mapping for a connector.
     * GET /connectors/{id}/account-mapping
     * API returns array of AccountConnector objects with external_account_id.
     * We convert to a simple { accountId: externalAccountId } map for the UI.
     */
    async fetchMapping(connectorId) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.get(
          `/connectors/${connectorId}/account-mapping`
        )
        const records = response.data.data || response.data || []
        // Convert array of AccountConnector records to { accountId: externalAccountId }
        const map = {}
        if (Array.isArray(records)) {
          records.forEach((record) => {
            if (record.account_id && record.external_account_id) {
              map[record.account_id] = record.external_account_id
            }
          })
        }
        this.mapping = map
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Save account mappings for a connector.
     * POST /connectors/{id}/account-mapping
     * @param {number|string} connectorId
     * @param {Object} mappings - { accountId: externalAccountId, ... }
     * Transforms to array format the backend expects.
     */
    async saveMapping(connectorId, mappings) {
      this.saving = true
      this.error = null
      try {
        // Transform { accountId: externalAccountId } to backend format
        const mappingsArray = Object.entries(mappings)
          .filter(([, externalId]) => externalId)
          .map(([accountId, externalAccountId]) => {
            // Find the connector account name for this external_id
            const connectorAccount = this.connectorAccounts.find(
              (ca) => ca.external_id === externalAccountId
            )
            return {
              account_id: Number(accountId),
              external_account_id: String(externalAccountId),
              external_account_name: connectorAccount?.name || null
            }
          })

        const response = await apiV1.post(
          `/connectors/${connectorId}/account-mapping`,
          { mappings: mappingsArray }
        )
        this.mapping = mappings
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.saving = false
      }
    },

    /**
     * Update a single account mapping in local state (before saving)
     */
    setLocalMapping(accountId, externalAccountId) {
      this.mapping = {
        ...this.mapping,
        [accountId]: externalAccountId
      }
    },

    clearMapping() {
      this.mapping = {}
      this.connectorAccounts = []
    },

    /**
     * Fetch credential status for a specific account under a connector.
     * GET /connectors/{connectorId}/accounts/{accountId}/credentials
     * Returns: { has_credentials, status, api_url, access_token (masked), external_account_id }
     */
    async getAccountCredentials(connectorId, accountId) {
      this.error = null
      try {
        const response = await apiV1.get(
          `/connectors/${connectorId}/accounts/${accountId}/credentials`
        )
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    /**
     * Save credentials for a specific account under a connector.
     * PUT /connectors/{connectorId}/accounts/{accountId}/credentials
     * @param {number|string} connectorId
     * @param {number|string} accountId
     * @param {Object} credentials - { api_url, access_token, external_account_id, ... }
     */
    async saveAccountCredentials(connectorId, accountId, credentials) {
      this.error = null
      try {
        const response = await apiV1.put(
          `/connectors/${connectorId}/accounts/${accountId}/credentials`,
          credentials
        )
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    /**
     * Fetch inventories (locations) from the connector for a specific account.
     * POST /connectors/{connectorId}/accounts/{accountId}/inventories
     */
    async fetchInventories(connectorId, accountId) {
      this.error = null
      try {
        const response = await apiV1.post(
          `/connectors/${connectorId}/accounts/${accountId}/inventories`
        )
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    /**
     * Test credentials by calling system info endpoint.
     * POST /connectors/{connectorId}/accounts/{accountId}/test-credentials
     */
    async testAccountCredentials(connectorId, accountId) {
      this.error = null
      try {
        const response = await apiV1.post(
          `/connectors/${connectorId}/accounts/${accountId}/test-credentials`
        )
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    clearError() {
      this.error = null
    }
  }
})
